import { Injectable } from '@angular/core';

/**
 * Service to manage image loading queue and prevent overwhelming the server
 * with simultaneous requests. Limits concurrent image loads to avoid performance issues.
 */
@Injectable({
	providedIn: 'root',
})
export class ImageLoadQueueService {
	private readonly MAX_CONCURRENT_LOADS = 5; // Maximum simultaneous image loads (increased for better performance)
	private readonly LOAD_DELAY_MS = 50; // Delay between loads in milliseconds (reduced for faster loading)
	private readonly REQUEST_COMPLETION_DELAY_MS = 100; // Delay before considering a request completed
	private activeLoads = 0;
	private loadQueue: Array<() => void> = [];
	private processingTimeout: ReturnType<typeof setTimeout> | null = null;

	/**
	 * Enqueues an image load request
	 * @param loadFn Function to execute when it's time to load the image
	 */
	enqueueLoad(loadFn: () => void): void {
		this.loadQueue.push(loadFn);
		this.scheduleProcess();
	}

	/**
	 * Schedules queue processing with throttling
	 */
	private scheduleProcess(): void {
		if (this.processingTimeout) {
			return; // Already scheduled
		}

		this.processingTimeout = setTimeout(() => {
			this.processingTimeout = null;
			this.processQueue();
		}, 50); // Small delay to batch multiple enqueue calls
	}

	/**
	 * Processes the load queue, respecting the maximum concurrent loads limit
	 */
	private processQueue(): void {
		while (this.activeLoads < this.MAX_CONCURRENT_LOADS && this.loadQueue.length > 0) {
			const loadFn = this.loadQueue.shift();
			if (loadFn) {
				this.activeLoads++;

				// Execute the load function with a staggered delay
				const delay = (this.MAX_CONCURRENT_LOADS - this.activeLoads) * this.LOAD_DELAY_MS;

				setTimeout(() => {
					try {
						loadFn();
					} catch (error) {
						console.error('Error loading image:', error);
					} finally {
						// Mark as completed after a delay to allow the HTTP request to start
						setTimeout(() => {
							this.activeLoads--;
							this.scheduleProcess();
						}, this.REQUEST_COMPLETION_DELAY_MS);
					}
				}, delay);
			}
		}
	}

	/**
	 * Gets the current queue size
	 */
	getQueueSize(): number {
		return this.loadQueue.length;
	}

	/**
	 * Gets the number of active loads
	 */
	getActiveLoads(): number {
		return this.activeLoads;
	}
}
